/**
 * Offline presenting readiness (v2.1 B14/D06). Pure: decides from the manifest the server
 * sent (`GET /designs/{d}/offline-manifest`) and what was actually stored and verified.
 *
 * Ready only when the scene of the manifest's revision, every font and every asset are
 * stored and their sha256/size matched the manifest. Anything missing or failed is listed,
 * and the bundle is then NOT ready — there is no "mostly ready".
 */
import type { OfflineManifest } from '../api/types.js';

export const SCENE_KEY = 'scene';

/** Stored-file record (bytes live next to it in the offline store). */
export interface StoredFileMeta {
  sha256: string;
  byteSize: number;
}

export interface OfflineBundleMeta {
  designId: string;
  /** The saved revision the bundle was built from (always manifest.revision). */
  revision: number;
  /** When the bundle finished downloading (ms since epoch). */
  savedAt: number;
  manifest: OfflineManifest;
  /** Scene JSON stored for manifest.revision. */
  sceneStored: boolean;
  /** Stored + verified files by key (`font:<path>` / `asset:<id>`). */
  files: Record<string, StoredFileMeta>;
  /** Download/verification failures by key, in Thai. */
  failures: Record<string, string>;
}

export type EntryState = 'stored' | 'missing' | 'failed';

export interface ReadinessEntry {
  kind: 'scene' | 'font' | 'asset';
  key: string;
  label: string;
  /** Expected size when the manifest says so. */
  bytes: number | null;
  state: EntryState;
  error?: string;
}

export interface Readiness {
  ready: boolean;
  revision: number | null;
  totalBytes: number;
  storedBytes: number;
  entries: ReadinessEntry[];
  /** Entries that are missing or failed (empty when ready). */
  problems: ReadinessEntry[];
}

/** Path part of a (possibly absolute) URL, used as a stable cache key. */
export function urlPath(url: string): string {
  try {
    const u = new URL(url, 'http://offline.invalid/');
    return u.pathname + u.search;
  } catch {
    return url;
  }
}

export const fontKey = (url: string) => `font:${urlPath(url)}`;
export const assetKey = (id: string) => `asset:${id}`;

/** "/v1/fonts/{id}/{weight}/{style}" → "ฟอนต์ น้ำหนัก 700" (+ " ตัวเอียง"). */
export function fontLabel(url: string): string {
  const m = /\/fonts\/[^/]+\/(\d+)\/(normal|italic)/.exec(urlPath(url));
  return m ? `ฟอนต์ น้ำหนัก ${m[1]}${m[2] === 'italic' ? ' ตัวเอียง' : ''}` : `ฟอนต์ ${urlPath(url)}`;
}

export function computeReadiness(meta: OfflineBundleMeta | null): Readiness {
  if (!meta) return { ready: false, revision: null, totalBytes: 0, storedBytes: 0, entries: [], problems: [] };
  const { manifest } = meta;
  const entries: ReadinessEntry[] = [];
  const state = (key: string, ok: boolean): Pick<ReadinessEntry, 'state' | 'error'> => {
    if (ok) return { state: 'stored' };
    const error = meta.failures[key];
    return error ? { state: 'failed', error } : { state: 'missing' };
  };

  entries.push({ kind: 'scene', key: SCENE_KEY, label: `ข้อมูลสไลด์ ฉบับที่ ${manifest.revision}`, bytes: null, ...state(SCENE_KEY, meta.sceneStored && meta.revision === manifest.revision) });
  for (const f of manifest.fonts) {
    const key = fontKey(f.url);
    const stored = meta.files[key];
    entries.push({ kind: 'font', key, label: fontLabel(f.url), bytes: stored?.byteSize ?? null, ...state(key, !!stored && stored.sha256 === f.sha256) });
  }
  for (const a of manifest.assets) {
    const key = assetKey(a.id);
    const stored = meta.files[key];
    entries.push({ kind: 'asset', key, label: `รูปภาพ ${a.id.slice(0, 8)}`, bytes: a.byteSize, ...state(key, !!stored && stored.sha256 === a.sha256 && stored.byteSize === a.byteSize) });
  }

  const problems = entries.filter((e) => e.state !== 'stored');
  const storedBytes = Object.values(meta.files).reduce((n, f) => n + f.byteSize, 0);
  return { ready: problems.length === 0, revision: manifest.revision, totalBytes: manifest.totalBytes, storedBytes, entries, problems };
}

/** The bundle is older than the design's current saved revision. */
export const isStale = (meta: OfflineBundleMeta | null, savedRevision: number | null) => !!meta && savedRevision !== null && meta.revision < savedRevision;
